import { api } from "./api";
import type { ZoneBoundaryPoint } from "@/utils/zoneBoundary";

export interface Zone {
  id: string;
  zoneNumber: number;
  name: string;
  description?: string | null;
  geographicSector?: string | null;
  deliveryOrder: number;
  deliveryCost?: number | null;
  centerLatitude?: number | null;
  centerLongitude?: number | null;
  radiusKm?: number | null;
  boundary?: ZoneBoundaryPoint[] | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  _count?: { customerAddresses?: number; deliveries: number };
}

export interface CreateZoneData {
  zoneNumber: number;
  name: string;
  description?: string;
  geographicSector?: string;
  deliveryOrder: number;
  deliveryCost?: number | null;
  boundary: ZoneBoundaryPoint[];
  isActive?: boolean;
}

export interface UpdateZoneData extends Partial<CreateZoneData> {}

/**
 * Service pour la gestion des zones
 */
export const zoneService = {
  /**
   * Récupère toutes les zones (avec compteurs clients/livraisons)
   */
  async getAll(includeInactive = false): Promise<Zone[]> {
    const response = await api.get<{ success: boolean; data: Zone[] }>(
      "/zones",
      { params: { includeInactive: includeInactive ? "true" : "false" } },
    );
    return response.data.data;
  },

  /**
   * Récupère une zone par son ID
   */
  async getById(id: string): Promise<Zone> {
    const response = await api.get<{ success: boolean; data: Zone }>(
      `/zones/${id}`,
    );
    return response.data.data;
  },

  /**
   * Crée une nouvelle zone
   */
  async create(data: CreateZoneData): Promise<Zone> {
    const response = await api.post<{ success: boolean; data: Zone }>(
      "/zones",
      data,
    );
    return response.data.data;
  },

  /**
   * Met à jour une zone
   */
  async update(id: string, data: UpdateZoneData): Promise<Zone> {
    const response = await api.put<{ success: boolean; data: Zone }>(
      `/zones/${id}`,
      data,
    );
    return response.data.data;
  },

  /**
   * Supprime une zone
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/zones/${id}`);
  },

  /** Prévisualise la zone correspondant à une adresse. */
  async resolveFromAddress(payload: {
    address: string;
    postalCode?: string | null;
    city?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  }): Promise<Zone> {
    const response = await api.post<{ success: boolean; data: Zone }>("/zones/resolve", payload);
    return response.data.data;
  },
};
